import React, { useCallback, useState } from 'react';
import { bool } from 'prop-types';
import _reduce from 'lodash/reduce';
import { Row, Col } from '@thd-olt-component-react/core-ui';
import { Button, useBreakpoint } from '@one-thd/sui-atomic-components';
import {
  useDropZoneStore,
  usePromoStore,
  useDigitalDataStore,
  useProductDataStore,
  useDimensionsStore,
  useProductInfoStore,
} from '../../../store';
import { useDesignStore } from '../../../store/design/context';
import logNewRelicCustom from '../../../utils/logNewRelicCustom';
import ConfirmRestartModal from './ConfirmRestartModal';
import { ANALYTICS_EVENTS, NEW_RELIC_EVENTS, STACK_BUILDER } from '../../../constants';

const ActionBar = ({ hasProducts, hasLineItems, disableBuyNow = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const storeProductData = useProductDataStore((state) => state.productData);
  const productInfo = useProductInfoStore((state) => state.productInfo);
  const undoAdd = useDesignStore((state) => state.undo);
  const resetDesign = useDesignStore((state) => state.reset);
  const { products } = useDesignStore((state) => state.design);
  const resetBundlesAdded = useDesignStore((state) => state.resetBundlesAdded);

  const resetDropZones = useDropZoneStore((state) => state.resetDropZones);

  const resetPromoItems = usePromoStore((state) => state.resetPromoItems);
  const resetDiscounts = usePromoStore((state) => state.resetDiscounts);
  const openDrawer = usePromoStore((state) => state.openDrawer);
  const fulfillmentProducts = usePromoStore((state) => state.fulfillmentProducts);

  const trackRemoveItemsFromStackEvent = useDigitalDataStore((state) => state.trackRemoveItemsFromStackEvent);
  const trackModalLinkClickEvent = useDigitalDataStore((state) => state.trackModalLinkClickEvent);

  const stackDimensions = useDimensionsStore((state) => state.stackDimensions);

  const matches = useBreakpoint('lg');
  const isDesktop = matches.greater;

  const handleOpenDrawer = useCallback(() => {
    openDrawer();
    trackModalLinkClickEvent(
      ANALYTICS_EVENTS.BUY_NOW,
      stackDimensions,
      `${productInfo?.brand} ${STACK_BUILDER.TYPE}`
    );
    logNewRelicCustom(NEW_RELIC_EVENTS.LOAD_STACK_SUMMARY, fulfillmentProducts);
    logNewRelicCustom(NEW_RELIC_EVENTS.LOAD_STACK_DIMENSIONS, stackDimensions);
  }, [stackDimensions, fulfillmentProducts]);

  const handleReset = useCallback(() => {
    // eslint-disable-next-line max-len
    const productArray = products.map((product) => ({ sku: product.Sku, basePrice: storeProductData?.[product.Sku]?.pricing?.value }));
    trackRemoveItemsFromStackEvent(ANALYTICS_EVENTS.RESTART_BUTTON, productArray);
    resetBundlesAdded();
    resetDesign();
    resetDropZones();
    resetPromoItems();
    resetDiscounts();
    return setIsModalOpen(false);
  }, [storeProductData, products]);

  const handleUndo = useCallback(() => {
    const lastProduct = products[products.length - 1];
    const productArray = [{ sku: lastProduct.Sku, basePrice: storeProductData?.[lastProduct.Sku]?.pricing?.value }];
    trackRemoveItemsFromStackEvent(ANALYTICS_EVENTS.UNDO_BUTTON, productArray);
    undoAdd();
    if (products.length === 1) {
      resetDiscounts();
    }
  }, [storeProductData, products]);

  const handleModalOpen = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  if (!hasProducts && !hasLineItems) {
    return null;
  }

  return (
    <div style={{ zIndex: 2 }}>
      <Row>
        <Col fallback="3" xs="3">
          <Button
            onClick={handleUndo}
            variant="secondary"
            fullWidth
            disabled={!hasProducts}
          >
            Undo
          </Button>
        </Col>
        <Col fallback="6" xs="6">
          {isDesktop && (
            <Button fullWidth onClick={handleOpenDrawer} disabled={disableBuyNow}>
              Buy Now
            </Button>
          )}
        </Col>
        <Col fallback="3" xs="3">
          <Button onClick={handleModalOpen} variant="secondary" fullWidth>Restart</Button>
        </Col>
      </Row>
      <ConfirmRestartModal isOpen={isModalOpen} setIsOpen={() => setIsModalOpen()} handleReset={handleReset} />
    </div>
  );
};

ActionBar.propTypes = {
  hasProducts: bool,
  hasLineItems: bool,
  disableBuyNow: bool
};

ActionBar.defaultProps = {
  hasProducts: false,
  hasLineItems: false,
  disableBuyNow: false
};

export default ActionBar;
